import * as yup from 'yup';

const textBlockDataSchema = yup.object().shape({
  paragraph: yup.string().nullable(),
  header: yup.string().nullable(),
  level: yup.number().nullable(),
  items: yup.array().of(yup.string()).nullable(),
  file: yup
    .object()
    .shape({
      url: yup.string(),
    })
    .nullable(),
  code: yup.string().nullable(),
  withBorder: yup.boolean().nullable(),
  withBackground: yup.boolean().nullable(),
  stretched: yup.boolean().nullable(),
  caption: yup.string().nullable(),
  style: yup.string().nullable(),
});

/**
 * Schema definition for a text block using Yup validation library.
 *
 * This schema validates the structure of a text block object, ensuring required fields
 * are present and content is non-empty based on the block type.
 *
 * The schema includes the following fields:
 * - id: A required string representing the block ID.
 * - type: A required string representing the block type.
 * - data: A nested schema that validates non-empty content specific to the block type.
 *
 * The `data` field validation is dynamic and based on the value of the `type` field. The
 * validation includes:
 * - Paragraph type requires non-empty `paragraph` field.
 * - Heading type requires non-empty `header` field.
 * - List type requires a non-empty array of non-empty items.
 * - Code type requires non-empty `code` field.
 * - Quote type requires non-empty `paragraph` field.
 * - Image type requires non-empty `file` URL.
 *
 * Compatible block types include: 'Paragraph', 'Heading', 'List', 'Code', 'Quote', and 'Image'.
 */
const textBlockSchema = yup.object().shape({
  id: yup.string().required('Block ID is required'),
  type: yup.string().required('Block type is required'),
  data: textBlockDataSchema.test(
    'non-empty-content',
    'Content cannot be empty',
    function (value) {
      if (this.parent.type === 'Paragraph') {
        return value.paragraph != null && value.paragraph.trim() !== '';
      }
      if (this.parent.type === 'Heading') {
        return value.header != null && value.header.trim() !== '';
      }
      if (this.parent.type === 'List') {
        return (
          value.items != null &&
          value.items.length > 0 &&
          value.items?.some((item) => item && item.trim() !== '')
        );
      }
      if (this.parent.type === 'Code') {
        return value.code != null && value.code.trim() !== '';
      }
      if (this.parent.type === 'Quote') {
        return value.paragraph != null && value.paragraph.trim() !== '';
      }
      if (this.parent.type === 'Image') {
        return (
          value.file != null &&
          value.file.url != null &&
          value.file.url.trim() !== ''
        );
      }
      return true;
    },
  ),
});

export const createPostSchema = yup.object().shape({
  userId: yup.string().required('User ID is required'),
  textBlocks: yup
    .array()
    .of(textBlockSchema)
    .min(1, 'At least one block is required'),
  article: yup.boolean().nullable(),
  username: yup.string().required('User Name is required'),
  status: yup.string().required('Status of Post is required'),
  categories: yup.array().of(yup.string()).required('Categories are required'),
});
